<template>
	<div class="pricingscheme" :class="{ 'active' : active }">
		<div class="pricing-header">
			<div class="price">
				<span class="currency">{{ pricing.currency }}</span>
				<span>{{ formatPrice(displayPrice) }}</span>
			</div>
			<div class="interval">
				{{ interval }}
			</div>
			<div class="period-price" v-if="waterPeriodPrice && waterPeriodPrice.period">
				{{ $t('pages.dashboard.subscriptions.period_price', {
					start: waterPeriodPrice.period.start_date,
					end: waterPeriodPrice.period.end_date
				}) }}
			</div>
		</div>
    <div class="pricing-body">
      <div class="ui divider horizontal">{{ $t('components.paymentplan.chosen_product') }}</div>
      <div class="product-block">
        <div class="product-price">
          <div class="name">{{ subscription.product.name }}:</div>
          <div class="price">{{ getProductPriceFromTotalPrice(subscription, pricing) }} {{ pricing.currency }}</div>
        </div>
        <div class="product-vat-price" v-if="subscription.product.apply_vat">
          <div class="name">25,00% moms:</div>
          <div class="price">{{ getProductPriceVatFromTotalPrice(subscription, pricing) }} {{ pricing.currency }}</div>
        </div>
      </div>
    </div>
		<div class="pricing-body" v-if="chosenAccessories && chosenAccessories.length > 0 || startUpFee">
			<div class="accessory" v-if="startUpFee">
				<div class="name">{{ $t('components.paymentplan.starting_fee') }}</div>
				<div class="price">{{ startUpFee }}</div>
			</div>
			<div class="ui divider horizontal" v-if="chosenAccessories && chosenAccessories.length">{{ $t('components.paymentplan.chosen_accessory') }}</div>
			<div class="accessory-block" v-for="accessory in chosenAccessories" :key="accessory.id" v-if="chosenAccessories && chosenAccessories.length">
        <div class="accessory-price">
          <div class="name">{{ accessory.name }}</div>
          <div class="price">{{ findAccessoryPrice(accessory) }} {{ pricing.currency }}</div>
        </div>
        <div class="accessory-vat-price" v-if="accessory.apply_vat">
          <div class="name">25,00% moms:</div>
          <div class="price">{{ findAccessoryPriceVat(accessory) }} {{ pricing.currency }}</div>
        </div>
			</div>
		</div>
		<div class="pricing-actions">
			<div class="ui blue circular button" @click="selectPaymentplan" v-if="!active">{{ $t('globals.buttons.select') }}</div>
			<div class="ui blue circular button" @click="deselectPaymentplan" v-else>{{ $t('globals.buttons.deselect') }}</div>
		</div>
	</div>
</template>

<script>
	import accounting from 'accounting'
	import find from 'lodash/find'

	export default {
		name: 'PaymentPlan',

		props: {
			pricing: {
				required: true,
				type: Object
			},

			active: {
				required: false,
				default: false,
				type: Boolean
			},

			subscription: {
				required: false,
				default: false,
				type: Object
			}
		},

		data () {
			return {
				colorClass: 'bluegreen-gradient'
			}
		},

		computed: {
			displayPrice () {
				return this.waterPeriodPrice
					? this.waterPeriodPrice.amount
					: this.pricing.price
			},

			waterPeriodPrice () {
				if (this.pricing.water_period_price) {
					return this.pricing.water_period_price
				}

				if (
					this.subscription &&
					this.subscription.water_period_price &&
					Number(this.subscription.option) === Number(this.pricing.option_id)
				) {
					return this.subscription.water_period_price
				}

				return null
			},

			interval () {
				let interval = ''

				if (this.pricing.type === 'instant') {
					interval = this.$t('components.paymentplan.intervals.instant')
				} else if (this.pricing.type === 'recurring') {
					switch (this.pricing.interval) {
						case '1mo':
							interval = this.$t('components.paymentplan.intervals.monthly')
							break
						case '3mo':
							interval = this.$t('components.paymentplan.intervals.quarterly')
							break
						case '6mo':
							interval = this.$t('components.paymentplan.intervals.biannually')
							break
						case '1y':
							interval = this.$t('components.paymentplan.intervals.annually')
							break
					}
				}

				return interval
			},

			chosenAccessories () {
				const returnAccessories = []

				if (
					this.subscription.accessories &&
					this.subscription.accessories !== null &&
					this.subscription.accessories !== 'null' &&
					typeof this.subscription.accessories !== undefined &&
					this.subscription.product &&
					this.subscription.product.accessories &&
					this.subscription.product.accessories.length > 0
				) {
					const chosenAcs = JSON.parse(this.subscription.accessories)

					$.map(chosenAcs, (currentValue, index) => {
						returnAccessories.push(find(this.subscription.product.accessories, {id: parseInt(index)}))
					})

					return returnAccessories
				} else {
					return []
				}
			},

			startUpFee () {
				if (
					this.subscription.starting_fee &&
					this.subscription.starting_fee !== null &&
					this.subscription.starting_fee !== 'null' &&
					this.pricing.currency &&
					this.pricing.currency !== null &&
					this.pricing.currency !== 'null'
				) {
					return accounting.formatMoney(this.subscription.starting_fee, this.pricing.currency, 2)
				} else {
					return false
				}
			}
		},

		methods: {
			findWaterPeriodItem (type, id) {
				if (!this.waterPeriodPrice || !this.waterPeriodPrice.items) {
					return null
				}

				return find(this.waterPeriodPrice.items, (item) => {
					return item.type === type && Number(item.id) === Number(id)
				}) || null
			},

			formatPrice (amount, minfrac = 2) {
				if (amount !== null) {
					return accounting.format(amount, minfrac)
				}
			},

			capitalizeFirst (string) {
				return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
			},

			selectPaymentplan () {
				this.$emit('selectpayplan', this.pricing.option_id, this.pricing.price, this.pricing.type)
			},

			deselectPaymentplan () {
				this.$emit('deselectpayplan', this.pricing.option_id)
			},

			findAccessoryPrice (accessory) {
				const waterPeriodItem = this.findWaterPeriodItem('accessory', accessory.id)
				if (waterPeriodItem) {
					return this.formatPrice(accessory.apply_vat
						? waterPeriodItem.gross_amount * 0.8
						: waterPeriodItem.gross_amount)
				}

				if (accessory && accessory.pricing) {
					let accessoryPricing = find(accessory.pricing, {type: this.pricing.type, interval: this.pricing.interval})

					if (accessoryPricing) {
            if (accessory.apply_vat) {
              return this.formatPrice(accessoryPricing.price * 0.8)
            } else {
              return this.formatPrice(accessoryPricing.price)
            }
					}
				}
			},

      findAccessoryPriceVat (accessory) {
				const waterPeriodItem = this.findWaterPeriodItem('accessory', accessory.id)
				if (waterPeriodItem) {
					return this.formatPrice(waterPeriodItem.gross_amount - (waterPeriodItem.gross_amount * 0.8))
				}

        if (accessory && accessory.pricing) {
          let accessoryPricing = find(accessory.pricing, {type: this.pricing.type, interval: this.pricing.interval})

          if (accessoryPricing) {
            return this.formatPrice(accessoryPricing.price - (accessoryPricing.price * 0.8))
          }
        }
      },

      getProductPriceFromTotalPrice (subscription, pricing) {
				const waterPeriodItem = this.findWaterPeriodItem('product', subscription.product.id)
				if (waterPeriodItem) {
					return this.formatPrice(subscription.product.apply_vat
						? waterPeriodItem.gross_amount * 0.8
						: waterPeriodItem.gross_amount)
				}

        if (subscription.accessories === 'null') {
          if (subscription.product.apply_vat) {
            return this.formatPrice(pricing.price * 0.8)
          } else {
            return this.formatPrice(pricing.price)
          }
        } else {
          let totalAccessoriesPrice = 0
          this.chosenAccessories.forEach((value, index) => {
            let accessoryPricing = find(value.pricing, {type: this.pricing.type, interval: this.pricing.interval})
            if (accessoryPricing) {
              totalAccessoriesPrice += accessoryPricing.price
            }
          })
          let price = pricing.price - totalAccessoriesPrice
          if (subscription.product.apply_vat) {
            return this.formatPrice(price * 0.8)
          } else {
            return this.formatPrice(price)
          }
        }
      },

      getProductPriceVatFromTotalPrice (subscription, pricing) {
				const waterPeriodItem = this.findWaterPeriodItem('product', subscription.product.id)
				if (waterPeriodItem) {
					return this.formatPrice(waterPeriodItem.gross_amount - (waterPeriodItem.gross_amount * 0.8))
				}

        if (subscription.accessories === 'null') {
          return this.formatPrice(pricing.price - (pricing.price * 0.8))
        } else {
          let totalAccessoriesPrice = 0
          this.chosenAccessories.forEach((value, index) => {
            let accessoryPricing = find(value.pricing, {type: this.pricing.type, interval: this.pricing.interval})
            totalAccessoriesPrice += accessoryPricing.price
          })
          let price = pricing.price - totalAccessoriesPrice
          let vatPrice = price - (price * 0.8)
          return this.formatPrice(vatPrice)
        }
      }
		}
	}
</script>

<style lang="scss">
	.pricingscheme {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-flow: column;
		flex-basis: 30%;
		margin: 1rem;
		position: relative;
		border-radius: 5px;
		overflow: hidden;
		background: #EBF0F6;
		border: 1px solid rgba(0,0,0,0);
		transition: all .6s ease;

		&.active {
			&:before {
				opacity: 1;
			}

			background: #00c1f7;

			.pricing-header {
				color: #fff;
			}
		}

		&:before {
			content: "";
			top: 0; bottom: 0; left: 0; right: 0;
			height: 100%;
			width: 100%;
			position: absolute;
			z-index: 0;
			opacity: 0;
			transition: all .6s ease;
			background: #00c1f7;
			background: -webkit-linear-gradient(20deg, #007ef3, #00c1f7);
			background: -o-linear-gradient(20deg, #007ef3, #00c1f7);
			background: linear-gradient(20deg, #007ef3, #00c1f7);
		}

		.pricing-header	{
			padding: 20px 0;
			width: 100%;
			display: flex;
			flex-wrap: wrap;
			align-items: center;
			justify-content: center;
			z-index: 5;

			.period-price {
				flex-basis: 100%;
				font-size: 0.8em;
				line-height: 1.4;
				margin-top: 12px;
				padding: 0 10px;
				text-align: center;
			}

			.price {
				display: flex;
				position: relative;
				flex-flow: column;
				align-items: flex-end;
				justify-content: center;
				font-size: 1.3em;
				height: auto;
				padding: 0 10px;

				.currency {
					font-size: 0.5em;
				}

				&:after {
					content: "/";
					position: absolute;
					// width: 100%;
					right: -5px;
					font-size: 1.6em;
				}
			}

			.interval {
				display: flex;
				align-items: center;
				justify-content: flex-end;
				height: auto;
				font-size: 1.3em;
				padding: 0 10px;
			}
		}

		.pricing-body {
			width: 100%;
			display: flex;
			align-items: center;
			flex-flow: column;
			background: #fff;
			border-top: 1px solid #ccc;
			border-bottom: 1px solid #ccc;
			z-index: 5;

			.accessory-block {
				display: block;
				padding: 10px 20px;
				width: 100%;
				border-bottom: 1px solid #ccc;

        .accessory-price, .accessory-vat-price {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
        }

				&:last-child {
					border-bottom: none;
					padding-bottom: 15px;
				}
			}

      .product-block {
        display: block;
        padding: 10px 20px;
        width: 100%;
      }

      .product-price, .product-vat-price {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
      }
		}

		.pricing-actions {
			width: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 10px;
			z-index: 5;
		}
	}
</style>



// WEBPACK FOOTER //
// src/components/payment/PaymentPlan.vue